<script setup lang="ts">

import { computed, defineEmits, ref } from 'vue'
import CircleLineJson from '@/assets/lotties/circle-line-explosion.json'
import { Vue3Lottie } from 'vue3-lottie'

const props = withDefaults(defineProps<{
	show: boolean
	imageSrc?: string
	imageRotateAngle?: string
	count?: number
	loop?: number | boolean
	reverse?: boolean // New prop to control direction
}>(), {
  imageSrc: undefined,
  imageRotateAngle: '90deg',
  count: undefined,
  loop: false,
  reverse: false,
})

const emit = defineEmits(['animationEnd'])

const showEmojis = ref(false)
const numberOfEmojis = computed(() => props.count ?? 8)
const explosionAngles = computed(() => {
  return Array.from({ length: numberOfEmojis.value }, (_, i) => {
    const baseAngle = (360 / numberOfEmojis.value) * i
    const baseAngleNum = Number(props.imageRotateAngle?.replace('deg', '') || 90)
    return {
      angle: `${baseAngle}deg`,
      bounceLeft: `${baseAngleNum - 30}deg`,
      bounceRight: `${baseAngleNum + 20}deg`,
    }
  })
})

const startEmojiExplosion = () => {
  setTimeout(() => {
    showEmojis.value = true
  }, props.reverse ? 1000 : 500)

  // Clean up after animation
  setTimeout(() => {
    showEmojis.value = false
  }, 3000)
}

const emojiAnimationClass = computed(() => props.reverse ? 'explosion-reverse' : 'explosion')

const finishedCount = ref(0)

function onEmojiAnimationEnd() {
  finishedCount.value++
  if (finishedCount.value >= numberOfEmojis.value) {
    emit('animationEnd')
    finishedCount.value = 0
  }
}

</script>

<template>
  <Transition name="fade">
    <Vue3Lottie
      v-if="show"
      :animation-data="CircleLineJson"
      :auto-play="true"
      :loop="loop"
      :delay="500"
      height="100%"
      :speed="1.2"
      class="pointer-events-none absolute left-1/2 top-1/2 z-[1000] min-h-[140px] min-w-[140px] -translate-x-1/2 -translate-y-1/2"
      @on-animation-loaded="startEmojiExplosion"
    />
  </Transition>

  <Transition name="fade">
    <div
      v-if="showEmojis && imageSrc"
      class="pointer-events-none absolute left-1/2 top-1/2 h-[200px] w-[200px] -translate-x-1/2 -translate-y-1/2"
    >
      <div
        v-for="(item, index) in explosionAngles"
        :key="index"
        :class="['absolute', showEmojis ? 'emoji-wrapper' : 'hidden']"
        :style="{
          '--angle': item.angle,
          '--distance': '60px',
          '--imgAngle': imageRotateAngle,
          '--imgAngleBounceLeft': item.bounceLeft,
          '--imgAngleBounceRight': item.bounceRight,
        }"
      >
        <img
          :src="imageSrc"
          :class="['emoji-particle', emojiAnimationClass, { hidden: !showEmojis }]"
          :style="{
            'animationDuration': props.reverse ? '1s' : '2s',
          }"
          @animationend="onEmojiAnimationEnd"
        >
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.emoji-particle {
	position: absolute;
	width: 28px;
	height: 28px;
	animation: ease-out forwards;
	opacity: 0;
	transform: scale(0.01) translateZ(1px);
	will-change: transform, opacity;
}

.emoji-particle.explosion {
	animation-name: explosion;
}

.emoji-particle.explosion-reverse {
	animation-name: explosion-reverse;
}

@keyframes explosion {
	0% {
		transform: translate(-50%, -50%) rotate(90deg) scale(0.01) translateZ(1px);
		opacity: 0;
	}
	20% {
		opacity: 0;
		transform: rotate(var(--imgAngle)) scale(0.01) translateZ(1px);
	}
	50% {
		transform: rotate(var(--imgAngle)) scale(1) translateZ(1px);
	}
	75% {
		opacity: 1;
	}
	100% {
		transform: translate(calc(-50% + var(--distance)), calc(-50% + var(--distance))) rotate(var(--imgAngle)) scale(0.5) translateZ(1px);
		opacity: 0;
	}
}

@keyframes explosion-reverse {
	0% {
		transform: translate(calc(-50% + var(--distance)), calc(-50% + var(--distance))) rotate(var(--imgAngle)) scale(0.5) translateZ(1px);
		opacity: 0;
	}
	10% {
		transform: translate(calc(-50% + var(--distance)), calc(-50% + var(--distance))) rotate(var(--imgAngleBounceLeft)) scale(1.2) translateZ(1px);
		opacity: 1;
	}
	18% {
		transform: translate(calc(-50% + var(--distance)), calc(-50% + var(--distance))) rotate(var(--imgAngleBounceRight)) scale(0.85) translateZ(1px);
		opacity: 1;
	}
	24% {
		transform: translate(calc(-50% + var(--distance)), calc(-50% + var(--distance))) rotate(var(--imgAngle)) scale(1.05) translateZ(1px);
		opacity: 1;
	}
	30% {
		transform: translate(calc(-50% + var(--distance)), calc(-50% + var(--distance))) rotate(var(--imgAngle)) scale(1) translateZ(1px);
		opacity: 1;
	}
	100% {
		transform: translate(-50%, -50%) rotate(90deg) scale(0.01) translateZ(1px);
		opacity: 0;
	}
}

.emoji-wrapper {
	position: absolute;
	left: 50%;
	top: 50%;
	width: 28px;
	height: 28px;
	transform-origin: center;
	transform: translate(-50%, -50%) rotate(var(--angle));
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

</style>